import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import type { WebhookSecretCreatedData } from '@/lib/crm/components/webhooks/WebhookSecretCreatedModal';
import { WebhookSecretCreatedModal } from '@/lib/crm/components/webhooks/WebhookSecretCreatedModal';
import { useObjectTypes } from '@/lib/crm/hooks/useObjectTypes';
import { logger } from '@/lib/logger';
import { getSnowflakeActionError } from '@/lib/portfolio/errors';
import { useApproveSnowflakeRequest } from '@/lib/portfolio/hooks/useApproveSnowflakeRequest';
import {
  availableV1TypeNames,
  SNOWFLAKE_V1_TYPES,
} from '@/lib/portfolio/snowflake-selection';
import type { ApproveSnowflakePayload } from '@/lib/portfolio/snowflake-types';

import { SnowflakeTypeSelectionPicker } from './SnowflakeTypeSelectionPicker';

const APPROVE_HEADER = (
  <>
    <h2 className='text-heading-3 text-[var(--color-text-primary)]'>
      Approve connector request
    </h2>
    <p className='text-sm text-[var(--color-text-tertiary)] mt-1'>
      Name the connector service account and choose what it can read.
    </p>
  </>
);

const SECRET_MODAL_INSTRUCTION =
  "This is the Snowflake connector credential. It's shown once – if lost, you must regenerate it.";

export interface SnowflakeApproveSlideoutProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: number;
  requestId: number;
}

/** Pre-filled service account name, so the slideout opens ready to submit. */
const DEFAULT_SERVICE_ACCOUNT_NAME = 'Snowflake connector';

/**
 * All v1 type names selected by default. The common "share everything
 * permitted" path is one click — admin can deselect to restrict.
 */
const ALL_V1_TYPE_NAMES: string[] = SNOWFLAKE_V1_TYPES.map(
  t => t.entityTypeName
);

const EMPTY_SELECTION_MESSAGE =
  'Select at least one data type to approve this connection.';

interface SnowflakeApproveFormProps {
  orgId: number;
  onSubmit: (payload: ApproveSnowflakePayload) => void;
  isSubmitting: boolean;
}

/**
 * The approval form body. Extracted so it mounts only while the slideout is
 * open (the parent renders it behind `isOpen`).
 *
 * The approve mutation deliberately lives in the always-mounted parent, NOT
 * here: if the slideout closes mid-flight this form unmounts, and a mutation
 * owned here would drop its `onSuccess`/`onError` — losing the one-time
 * secret display. The async approve call is handed up via `onSubmit`.
 */
function SnowflakeApproveForm({
  orgId,
  onSubmit,
  isSubmitting,
}: SnowflakeApproveFormProps) {
  const [name, setName] = useState(DEFAULT_SERVICE_ACCOUNT_NAME);
  const [selectedTypeNames, setSelectedTypeNames] =
    useState<string[]>(ALL_V1_TYPE_NAMES);

  const { data: objectTypesData } = useObjectTypes(orgId);
  // Stable Set of type names the org actually has. undefined while loading
  // (fallback: treat all as available). Empty Set means org has none.
  const availableTypeNames = useMemo(
    () => availableV1TypeNames(objectTypesData?.allObjectTypes),
    [objectTypesData]
  );

  // Effective selection excludes types absent from the org's schema.
  // Computed at render time — no useEffect rewrite needed.
  const effectiveSelected =
    availableTypeNames === undefined
      ? selectedTypeNames
      : selectedTypeNames.filter(n => availableTypeNames.has(n));

  const handleNameChange = useCallback((value: string) => {
    setName(value);
  }, []);

  const handleSelectionChange = useCallback((next: string[]) => {
    setSelectedTypeNames(next);
  }, []);

  const handleSubmit = useCallback(() => {
    onSubmit({
      service_account_name: name.trim(),
      selected_type_names: effectiveSelected,
    });
  }, [effectiveSelected, name, onSubmit]);

  const isEmpty = effectiveSelected.length === 0;
  const isSubmitDisabled = !name.trim() || isEmpty || isSubmitting;

  return (
    <div className='flex flex-col gap-4 pb-6'>
      <Input
        label='Name'
        value={name}
        onChange={handleNameChange}
        isRequired
        maxLength={255}
        size='md'
      />
      <SnowflakeTypeSelectionPicker
        orgId={orgId}
        value={selectedTypeNames}
        onChange={handleSelectionChange}
        availableTypeNames={availableTypeNames}
      />
      {isEmpty && (
        <p className='text-sm text-[var(--color-text-error-primary)]'>
          {EMPTY_SELECTION_MESSAGE}
        </p>
      )}
      <Button
        color='primary'
        onClick={handleSubmit}
        isDisabled={isSubmitDisabled}
        isLoading={isSubmitting}
      >
        Approve request
      </Button>
    </div>
  );
}

export function SnowflakeApproveSlideout({
  isOpen,
  onOpenChange,
  orgId,
  requestId,
}: SnowflakeApproveSlideoutProps) {
  const [secretData, setSecretData] = useState<WebhookSecretCreatedData | null>(
    null
  );

  const handleCloseSecretModal = useCallback(() => {
    setSecretData(null);
  }, []);

  // Guard against a double-set: keep the first minted secret if one is
  // somehow reported twice before the modal is dismissed.
  const handleSecretMinted = useCallback((data: WebhookSecretCreatedData) => {
    setSecretData(prev => prev ?? data);
  }, []);

  // The approve mutation is owned here, in the always-mounted wrapper, rather
  // than in the form body. If the user closes the slideout while an approve
  // is in flight the form unmounts; a mutation owned there would drop its
  // onSuccess/onError, losing the one-time secret display. Owning it here
  // keeps that reliable regardless of the form's mount state.
  const approveMutation = useApproveSnowflakeRequest(orgId);

  const handleSubmit = useCallback(
    (payload: ApproveSnowflakePayload) => {
      approveMutation.mutate(
        { requestId, payload },
        {
          onSuccess: res => {
            handleSecretMinted({
              bearer_token: res.token,
              secret_hint: res.masked_hint,
              secret_entity_id: res.service_account_entity_id,
            });
            onOpenChange(false);
          },
          onError: err => {
            // Surfaced inline against this request: toast and keep the
            // slideout open so the admin can retry rather than losing their
            // in-progress selection.
            logger.error(
              "Couldn't approve Snowflake connection",
              err instanceof Error ? err : new Error(String(err)),
              { component: 'portfolio', org_id: orgId, request_id: requestId }
            );
            toast.error(getSnowflakeActionError(err));
          },
        }
      );
    },
    [approveMutation, requestId, handleSecretMinted, onOpenChange, orgId]
  );

  return (
    <>
      <SlideoutMenu
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        header={APPROVE_HEADER}
      >
        {isOpen && (
          <SnowflakeApproveForm
            orgId={orgId}
            onSubmit={handleSubmit}
            isSubmitting={approveMutation.isPending}
          />
        )}
      </SlideoutMenu>
      <WebhookSecretCreatedModal
        isOpen={!!secretData}
        onClose={handleCloseSecretModal}
        data={secretData}
        instruction={SECRET_MODAL_INSTRUCTION}
      />
    </>
  );
}
